import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { EntityValuationActions_EntityFragment } from '@/modules/entities/details/EntityValuation/graphql/EntityValuation.generated';
import { SlideToolbarButton } from '@/modules/slideEditor/SlideToolbar';
import { EntityKind } from '@/types/schema';

import { PresentationViewSource_EntityFragment } from '../../../presentationEntityActions/graphql/PresentationViewSource.generated';
import {
  EditEntityButton,
  EditHoldingsButton,
  ViewSourceButton,
} from '../../../presentationEntityActions/PresentationEntityActionButtons';
import {
  PresentationEntityActionTarget,
  usePresentationEntityActionsContext,
} from '../../../presentationEntityActions/presentationEntityActions.context';
import { getEntityViewSourceCitation } from '../../../presentationEntityActions/presentationViewSource.utils';
import { getEntityCoverGrayBlockEditConfig } from './entityCoverGrayBlock.utils';

interface EntityCoverHeaderToolbarProps {
  target: PresentationEntityActionTarget;
  /**
   * Carries the slim holdings slice the "Edit holdings" menu is built from, plus
   * the citation slice "View source" opens. Both ride on the entity the slide
   * already holds, so this toolbar works on surfaces without the designer
   * context (the entity preview modal).
   */
  entity: EntityValuationActions_EntityFragment &
    PresentationViewSource_EntityFragment;
  /** Hide the toolbar once an action runs. */
  onDismiss: () => void;
}

/**
 * Toolbar shown over the cover slide's title/icons section: the same
 * "Edit entity", "Edit holdings", and "View source" actions offered on each
 * entities-list row, driven by the shared `PresentationEntityActionsContext`.
 */
export function EntityCoverHeaderToolbar({
  target,
  entity,
  onDismiss,
}: EntityCoverHeaderToolbarProps) {
  const actions = usePresentationEntityActionsContext();
  if (!actions) return null;

  return (
    <>
      <EditEntityButton target={target} onDismiss={onDismiss} />
      <EditHoldingsButton
        target={target}
        entity={entity}
        onDismiss={onDismiss}
      />
      <ViewSourceButton
        recordId={entity.id}
        citation={getEntityViewSourceCitation(entity)}
        onDismiss={onDismiss}
      />
    </>
  );
}

interface EntityCoverGrayBlockToolbarProps {
  target: PresentationEntityActionTarget;
  /** Drives the entity-category label and the section the button opens. */
  entityKind: EntityKind;
  onDismiss: () => void;
}

/**
 * Toolbar shown over the cover slide's gray "details" block. A single button
 * whose label matches the people listed in that block (trustees, key people,
 * or controlling persons/signatories) and which deep-links into the matching
 * edit-entity section.
 */
export function EntityCoverGrayBlockToolbar({
  target,
  entityKind,
  onDismiss,
}: EntityCoverGrayBlockToolbarProps) {
  const actions = usePresentationEntityActionsContext();
  const onEditEntitySection = actions?.onEditEntitySection;
  const config = getEntityCoverGrayBlockEditConfig(entityKind);

  if (!config) return null;

  return (
    <SlideToolbarButton
      variant="transparent"
      startIcon={Edit02Icon}
      disabled={!onEditEntitySection}
      onClick={
        onEditEntitySection
          ? () => {
              onEditEntitySection(target, config.section);
              onDismiss();
            }
          : undefined
      }
    >
      {config.label}
    </SlideToolbarButton>
  );
}
